/**
 * Renders a high-visibility banner on every page when the user is NOT on the
 * production hostname. Goal: make it impossible for anyone to confuse the
 * UAT / preprod / local-dev build with the live production environment.
 *
 * Detection is hostname-based and runtime-only — no env-var wiring needed.
 * Prod lives at atlas.jmtest.com; everything else shows the banner. This
 * deliberately includes local dev (`localhost`, `127.0.0.1`) and any
 * `*.azurestaticapps.net` preview URL.
 */
import { useState } from 'react';

const PROD_HOSTNAMES = new Set<string>([
  'atlas.jmtest.com',
]);

function detectEnv(): 'prod' | 'preprod' | 'local' | 'unknown' {
  if (typeof window === 'undefined') return 'unknown';
  const host = window.location.hostname.toLowerCase();
  if (PROD_HOSTNAMES.has(host)) return 'prod';
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return 'local';
  return 'preprod';
}

const LABELS: Record<Exclude<ReturnType<typeof detectEnv>, 'prod'>, string> = {
  preprod: 'TEST ENVIRONMENT — UAT / Pre-production',
  local:   'LOCAL DEV BUILD',
  unknown: 'NON-PRODUCTION BUILD',
};

const SUBTEXT: Record<Exclude<ReturnType<typeof detectEnv>, 'prod'>, string> = {
  preprod: 'Data, settings, and access here do not reflect production. Any changes you make here are isolated to UAT.',
  local:   'You are running a local development build. Data is not shared with production.',
  unknown: 'This is not the production Atlas. Verify the URL before relying on the data you see here.',
};

export default function EnvBanner() {
  const env = detectEnv();
  const [dismissed, setDismissed] = useState(false);

  if (env === 'prod' || dismissed) return null;

  const label = LABELS[env];
  const subtext = SUBTEXT[env];

  return (
    <div
      role="alert"
      aria-live="polite"
      className="w-full bg-amber-400 text-amber-950 border-b-2 border-amber-600 shadow-sm"
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-2 flex items-center gap-3">
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 0 0 1.74-3l-6.93-12a2 2 0 0 0-3.48 0l-6.93 12a2 2 0 0 0 1.74 3z" />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="font-montserrat font-bold text-[13px] tracking-wide uppercase leading-tight">
            {label}
          </p>
          <p className="text-[11.5px] font-work-sans leading-snug">{subtext}</p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-amber-950/70 hover:text-amber-950 p-1 rounded hover:bg-amber-500/30 transition-colors"
          aria-label="Dismiss test-environment banner"
          title="Dismiss for this session"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
